import { useState, useMemo } from 'react';

import { pdf } from '@react-pdf/renderer';
import PropTypes from 'prop-types';

import useEnv from '@zenbusiness/application-commons-env';
import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';

import * as ROUTES from '#client/constants/routes';
import { useCustomForm } from '#client/contexts/CustomFormContext';

import * as Styled from './BusinessIdeaResults.style';
import PoweredByVelo from '../PoweredByVelo/PoweredByVelo';
import { ReportPanel } from '../ReportLayout/ReportPanel';
import SelectionPanel from '../ReportLayout/SelectionPanel';
import SidePanel from '../ReportLayout/SidePanel';
import { BUSINESS_IDEA_SECTIONS } from '../utils/constants';
import { BusinessIdeaPDF } from './components/BusinessIdeaResultPDF/BusinessIdeaResultPDF';
import { BusinessIdeaResultsData } from './types';

/**
 * Business Results component - REFACTORED VERSION
 * Displays personalized business ideas and detailed reports
 * Uses enhanced shared ReportPanel
 *
 * This is a refactored version for comparison testing.
 * Original component remains unchanged until verification is complete.
 *
 * @param {object} props - Component props
 * @param {object} props.data - Business results data containing ideas and user profile
 * @param {Function} [props.onComponentClick] - Callback for component actions (follows assistant-ui pattern)
 * @component
 */
function BusinessIdeaResults({ data, onComponentClick }) {
  const { DEPLOY_ENV } = useEnv('head-first');
  const { answers, isLoading, setLoading, resetForm, clearAnswers } = useCustomForm();

  // Support both data.ideas and data.items - memoize to prevent dependency issues
  const items = useMemo(() => data.ideas || data.items || [], [data.ideas, data.items]);

  const [selectedIdeaId, setSelectedIdeaId] = useState(data.selectedIdeaId || items[0]?.id);

  // Find the currently selected idea
  const selectedIdea = useMemo(
    () => items.find((idea) => idea.id === selectedIdeaId),
    [items, selectedIdeaId]
  );

  const handleIdeaSelect = (ideaId) => {
    setSelectedIdeaId(ideaId);
  };

  const handleDownloadReport = async () => {
    if (isLoading) return;

    setLoading(true);

    try {
      const blob = await pdf(
        <BusinessIdeaPDF environment={DEPLOY_ENV} idea={selectedIdea} />
      ).toBlob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedIdea.title.replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[BusinessResults] Download report error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMore = async () => {
    if (isLoading) return;

    setLoading(true);
    if (onComponentClick) {
      await onComponentClick({
        type: 'business_results_generate_more',
        payload: {
          answers
        }
      });
    }
    setLoading(false);
  };

  const handleStartOver = async () => {
    if (isLoading) return;

    setLoading(true);
    if (onComponentClick) {
      await resetForm();
      await clearAnswers();
      await onComponentClick({ type: 'business_results_start_over' });
    }
    setLoading(false);
  };

  // Handle case where no ideas are provided
  if (!items || items.length === 0) {
    return (
      <Styled.BusinessIdeaResultsRoot>
        <Box style={{ textAlign: 'center' }}>
          <Text>No business ideas found</Text>
          <Text>
            We weren't able to generate business ideas based on your responses. Please try again.
          </Text>
        </Box>
      </Styled.BusinessIdeaResultsRoot>
    );
  }

  return (
    <Styled.BusinessIdeaResultsRoot>
      <Styled.IdeasHeader>
        <Text size="md" weight="medium">
          Your personalized business ideas
        </Text>
        <Text size="sm">
          Based on your responses, here are business ideas tailored specifically for you.
        </Text>
      </Styled.IdeasHeader>
      <Styled.IdeasContent>
        <SidePanel
          selectionPanel={
            <SelectionPanel
              clickstreamPrefix="business_idea"
              generateMoreText="Generate more ideas"
              getDisplayTitle={(idea) => idea.title}
              getItemId={(idea) => idea.id}
              items={items}
              loading={isLoading}
              selectedItemId={selectedIdeaId}
              startOverText="Start over"
              onGenerateMore={handleGenerateMore}
              onItemSelect={handleIdeaSelect}
              onStartOver={handleStartOver}
            />
          }
        />

        <ReportPanel
          loading={isLoading}
          reportData={selectedIdea}
          sectionsConfig={BUSINESS_IDEA_SECTIONS}
          onDownloadReport={handleDownloadReport}
        />
      </Styled.IdeasContent>
      <PoweredByVelo url={ROUTES.website(DEPLOY_ENV)} />
    </Styled.BusinessIdeaResultsRoot>
  );
}

BusinessIdeaResults.propTypes = {
  data: BusinessIdeaResultsData,
  onComponentClick: PropTypes.func
};

export default BusinessIdeaResults;
